/**
 * Storage Service Interface
 * Abstraction for storage operations (localStorage, sessionStorage, etc.)
 * Follows Dependency Inversion Principle
 */
export interface IStorageService {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  key(index: number): string | null;
  get length(): number;

  getJSON<T>(key: string): T | null;
  setJSON<T>(key: string, value: T): void;
}


/**
 * LocalStorage Implementation
 * Provides localStorage abstraction for better testability and maintainability
 */
export class LocalStorageService implements IStorageService {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Failed to get item from localStorage', error);
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('Failed to set item in localStorage', error);
      throw error;
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove item from localStorage', error);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear localStorage', error);
    }
  }

  key(index: number): string | null {
    try {
      return localStorage.key(index);
    } catch (error) {
      console.error('Failed to get key from localStorage', error);
      return null;
    }
  }

  get length(): number {
    try {
      return localStorage.length;
    } catch (error) {
      console.error('Failed to get localStorage length', error);
      return 0;
    }
  }

  /**
   * Get JSON object from storage
   */
  getJSON<T>(key: string): T | null {
    const item = this.getItem(key);
    if (!item) return null;
    try {
      return JSON.parse(item) as T;
    } catch (error) {
      console.error('Failed to parse JSON from localStorage', error);
      return null;
    }
  }

  /**
   * Set JSON object to storage
   */
  setJSON<T>(key: string, value: T): void {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to stringify JSON for localStorage', error);
      throw error;
    }
  }
}

/**
 * Storage Service Factory
 * Provides singleton instance of storage service
 */
class StorageServiceFactory {
  private static instance: IStorageService | null = null;

  static getInstance(): IStorageService {
    if (!this.instance) {
      this.instance = new LocalStorageService();
    }
    return this.instance;
  }

  static setInstance(service: IStorageService): void {
    this.instance = service;
  }
}

export const storageService = StorageServiceFactory.getInstance();

